import type { components } from "@brandex/api-client";
import { formatDateTime, formatNumber, type MessageKey } from "@brandex/i18n";
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from "@brandex/ui";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Download, ExternalLink } from "lucide-react";
import { useState } from "react";

import { useBrandex, useT } from "../context";
import { errorMessage } from "../errors";
import { useMyOrganizations } from "../queries";

type Report = components["schemas"]["MonthlyReportDto"];

const DHAKA = "Asia/Dhaka";

/** The current calendar month in Bangladesh time (UTC+6), `YYYY-MM`. */
export function dhakaMonth(now: Date = new Date()): string {
  return new Date(now.getTime() + 6 * 3_600_000).toISOString().slice(0, 7);
}

/** `YYYY-MM` shifted by `delta` months. */
export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}

const validMonth = (raw: string | null | undefined): raw is string => raw != null && /^\d{4}-(0[1-9]|1[0-2])$/.test(raw);

/**
 * "Monthly report" (F-14, ADR 0016 §3): summary, delivered vs package,
 * published posts, bookings per chamber, platform numbers (only from
 * connected sources) and next month. Shared by web and desktop; the admin
 * passes `orgId` to show a client's report. "Download PDF" prints it
 * through the browser, which shapes Bangla text correctly.
 */
export function ReportView({ month: initial, orgId: forOrg, heading }: { month?: string | null; orgId?: string; heading?: string }) {
  const t = useT();
  const { api, locale } = useBrandex();
  const current = dhakaMonth();
  const [month, setMonth] = useState(validMonth(initial) && initial <= current ? initial : current);
  const orgs = useMyOrganizations(forOrg === undefined);
  const orgId = forOrg ?? orgs.data?.[0]?.organization.id;
  const report = useQuery<Report>({
    queryKey: ["orgs", orgId ?? "", "report", month],
    enabled: orgId !== undefined,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/orgs/{org_id}/report", {
        params: { path: { org_id: orgId ?? "" }, query: { month } },
      });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const monthName = new Intl.DateTimeFormat(locale === "bn" ? "bn-BD" : "en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${month}-01T00:00:00Z`),
  );
  const r = report.data;
  return (
    <div className="mx-auto grid max-w-5xl gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{heading ?? t("report.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="hidden print:inline">{monthName} · </span>
            {t("report.intro")}
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Button size="sm" variant="outline" aria-label={t("report.prev")} onClick={() => setMonth((m) => shiftMonth(m, -1))}>
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          <span className="min-w-36 text-center font-medium" aria-live="polite">{monthName}</span>
          <Button size="sm" variant="outline" aria-label={t("report.next")} disabled={month >= current} onClick={() => setMonth((m) => shiftMonth(m, 1))}>
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
          <Button size="sm" variant="outline" disabled={r === undefined} onClick={() => window.print()}>
            <Download className="h-4 w-4" aria-hidden />
            {t("report.download")}
          </Button>
        </div>
      </header>
      {(forOrg === undefined && orgs.isPending) || report.isPending ? <Spinner label={t("common.loading")} /> : null}
      {orgs.isError || report.isError ? <Alert tone="error">{errorMessage(orgs.error ?? report.error, t)}</Alert> : null}
      {r ? <ReportBody r={r} /> : null}
    </div>
  );
}

function ReportBody({ r }: { r: Report }) {
  const t = useT();
  const { locale } = useBrandex();
  const n = (v: number) => formatNumber(locale, v);
  const final = r.status === "final";
  const stats: { key: MessageKey; value: number }[] = [
    { key: "report.approved", value: r.summary.approved },
    { key: "report.published", value: r.summary.published_posts },
    { key: "report.appointments", value: r.summary.appointments },
    { key: "report.confirmed", value: r.summary.confirmed },
    { key: "report.shootsHeld", value: r.summary.shoots_held },
  ];
  return (
    <>
      <Alert tone={final ? "success" : "info"}>
        <Badge tone={final ? "success" : "accent"}>{t(final ? "report.final" : "report.inProgress")}</Badge>{" "}
        {t(final ? "report.finalHint" : "report.inProgressHint")}
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>{t("report.summary")}</CardTitle>
          <CardDescription>{t("report.calculated")}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {stats.map((s) => (
              <div key={s.key} className="rounded-[var(--radius-lg)] border border-border p-3">
                <dt className="text-xs text-muted-foreground">{t(s.key)}</dt>
                <dd className="mt-1 font-display text-2xl font-bold tabular-nums">{n(s.value)}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("report.delivery")}</CardTitle>
          <CardDescription>{t("report.calculated")}</CardDescription>
        </CardHeader>
        <CardContent>
          {r.delivery.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("report.noDelivery")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2">{t("report.kind")}</th>
                    <th className="text-right">{t("report.committed")}</th>
                    <th className="text-right">{t("report.delivered")}</th>
                    <th className="text-right">{t("report.publishedCol")}</th>
                    <th className="text-right">{t("report.waiting")}</th>
                    <th className="pl-4" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {r.delivery.map((l) => (
                    <tr key={l.kind}>
                      <td className="py-2 font-medium">{t(`content.kind.${l.kind}` as MessageKey)}</td>
                      <td className="text-right tabular-nums">{n(l.committed)}</td>
                      <td className="text-right tabular-nums">{n(l.delivered)}</td>
                      <td className="text-right tabular-nums">{n(l.published)}</td>
                      <td className="text-right tabular-nums">{n(l.waiting_for_doctor)}</td>
                      <td className="pl-4">
                        {l.shortfall ? (
                          <Badge tone="warning">{t(`report.shortfall.${l.shortfall}` as MessageKey)}</Badge>
                        ) : l.committed > 0 ? (
                          <Badge tone="success">{t("report.onTrack")}</Badge>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("report.posts")}</CardTitle>
        </CardHeader>
        <CardContent>
          {r.posts.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("report.noPosts")}</p>
          ) : (
            <ul className="divide-y divide-border">
              {r.posts.map((p) => (
                <li key={`${p.deliverable_id}-${p.platform}`} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <span>
                    <span className="font-medium">{p.title}</span>
                    <span className="text-muted-foreground"> · {formatDateTime(locale, p.published_at, DHAKA)}</span>
                  </span>
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-azure-500 hover:underline">
                    {t(`content.platform.${p.platform}` as MessageKey)}
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("report.bookings")}</CardTitle>
            <CardDescription>{t("report.bookingsNote")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="grid gap-2 text-sm">
              {r.chambers.map((c) => (
                <li key={c.chamber_id} className="flex flex-wrap justify-between gap-2">
                  <span className="font-medium">{c.name}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {t("report.appointments")}: {n(c.appointments)} · {t("report.confirmed")}: {n(c.confirmed)} · {t("report.declined")}: {n(c.declined)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("report.platforms")}</CardTitle>
            <CardDescription>{t("report.platformsNote")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="grid gap-2 text-sm">
              {r.platforms.map((p) => (
                <li key={p.platform} className="grid gap-0.5">
                  <span className="flex justify-between gap-2">
                    <span className="font-medium">{t(`content.platform.${p.platform}` as MessageKey)}</span>
                    {!p.connected ? (
                      <span className="text-muted-foreground">{t("report.notConnected")}</span>
                    ) : p.metrics ? (
                      <span className="tabular-nums">
                        {[
                          p.metrics.followers != null ? t("report.followers", { n: n(p.metrics.followers) }) : null,
                          p.metrics.total_views != null ? t("report.views", { n: n(p.metrics.total_views) }) : null,
                          p.metrics.posts != null ? t("report.postsCount", { n: n(p.metrics.posts) }) : null,
                        ]
                          .filter((x) => x !== null)
                          .join(" · ")}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{t("report.unavailable")}</span>
                    )}
                  </span>
                  {p.metrics ? (
                    <span className="text-xs text-muted-foreground">
                      {t("report.source", { source: p.metrics.source, when: formatDateTime(locale, p.metrics.retrieved_at, DHAKA) })}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("report.nextMonth")}</CardTitle>
          <CardDescription>{t("report.planned", { n: n(r.next_planned) })}</CardDescription>
        </CardHeader>
        <CardContent>
          {r.next_shoots.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("report.noShoots")}</p>
          ) : (
            <ul className="grid gap-2 text-sm">
              {r.next_shoots.map((s) => (
                <li key={s.starts_at + s.kind}>
                  <span className="font-medium">{t(`content.shoot.kind.${s.kind}` as MessageKey)}</span>
                  <span className="text-muted-foreground"> · {formatDateTime(locale, s.starts_at, DHAKA)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">{formatDateTime(locale, r.generated_at, DHAKA)}</p>
    </>
  );
}
